import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import {
  remarketingSequencesAPI,
  remarketingEnrollmentsAPI,
} from '../../api/remarketingSequences';

export const state = {
  records: [],
  enrollments: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getAllSequences(_state) {
    return _state.records;
  },
  getSequence:
    _state =>
    id =>
      _state.records.find(r => r.id === Number(id)),
  getActiveSequences(_state) {
    return _state.records.filter(r => r.active);
  },
  getEnrollments(_state) {
    return _state.enrollments;
  },
  getEnrollmentsForConversation:
    _state =>
    conversationId =>
      _state.enrollments.filter(
        e => e.conversation_id === Number(conversationId)
      ),
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.SET_REMARKETING_UI_FLAG, { isFetching: true });
    try {
      const response = await remarketingSequencesAPI.get();
      commit(types.SET_REMARKETING_SEQUENCES, response.data);
    } catch (error) {
      // noop — UI handles empty list
    } finally {
      commit(types.SET_REMARKETING_UI_FLAG, { isFetching: false });
    }
  },

  show: async ({ commit }, id) => {
    const response = await remarketingSequencesAPI.show(id);
    commit(types.EDIT_REMARKETING_SEQUENCE, response.data);
    return response.data;
  },

  create: async ({ commit }, payload) => {
    commit(types.SET_REMARKETING_UI_FLAG, { isCreating: true });
    try {
      const response = await remarketingSequencesAPI.create(payload);
      commit(types.ADD_REMARKETING_SEQUENCE, response.data);
      return response.data;
    } finally {
      commit(types.SET_REMARKETING_UI_FLAG, { isCreating: false });
    }
  },

  update: async ({ commit }, { id, ...payload }) => {
    commit(types.SET_REMARKETING_UI_FLAG, { isUpdating: true });
    try {
      const response = await remarketingSequencesAPI.update(id, payload);
      commit(types.EDIT_REMARKETING_SEQUENCE, response.data);
      return response.data;
    } finally {
      commit(types.SET_REMARKETING_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async ({ commit }, id) => {
    commit(types.SET_REMARKETING_UI_FLAG, { isDeleting: true });
    try {
      await remarketingSequencesAPI.delete(id);
      commit(types.DELETE_REMARKETING_SEQUENCE, id);
    } finally {
      commit(types.SET_REMARKETING_UI_FLAG, { isDeleting: false });
    }
  },

  getEnrollments: async ({ commit }, params = {}) => {
    const response = await remarketingEnrollmentsAPI.get({
      params,
    });
    commit(types.SET_REMARKETING_ENROLLMENTS, response.data);
    return response.data;
  },

  enrollConversation: async (
    { commit },
    { conversationId, sequenceId }
  ) => {
    const response = await remarketingEnrollmentsAPI.enroll({
      conversationId,
      sequenceId,
    });
    commit(types.UPDATE_REMARKETING_ENROLLMENT, response.data);
    return response.data;
  },

  stopEnrollment: async ({ commit }, enrollmentId) => {
    const response = await remarketingEnrollmentsAPI.stop(enrollmentId);
    commit(types.UPDATE_REMARKETING_ENROLLMENT, response.data);
    return response.data;
  },
};

export const mutations = {
  [types.SET_REMARKETING_UI_FLAG](_state, flags) {
    _state.uiFlags = { ..._state.uiFlags, ...flags };
  },
  [types.SET_REMARKETING_SEQUENCES](_state, records) {
    _state.records = records;
  },
  [types.ADD_REMARKETING_SEQUENCE]: MutationHelpers.create,
  [types.EDIT_REMARKETING_SEQUENCE]: MutationHelpers.update,
  [types.DELETE_REMARKETING_SEQUENCE]: MutationHelpers.destroy,
  [types.SET_REMARKETING_ENROLLMENTS](_state, enrollments) {
    _state.enrollments = enrollments;
  },
  [types.UPDATE_REMARKETING_ENROLLMENT](_state, enrollment) {
    const idx = _state.enrollments.findIndex(e => e.id === enrollment.id);
    if (idx !== -1) {
      _state.enrollments.splice(idx, 1, enrollment);
    } else {
      _state.enrollments.unshift(enrollment);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
